import React, { useEffect, useRef, useState, useId } from 'react';
import { FileText, Microscope, AlertTriangle, Stethoscope, BookOpen, ClipboardCheck, AlertCircle, Pill, Phone, Clock, Image, ExternalLink, CalendarClock, Calculator } from 'lucide-react';
import { findRelevantImages } from '../data/medicalImages';
import { Link } from 'react-router-dom';
import { AIResponseDisclaimer } from './RegulatoryComponents';
import { RichText } from './AIResultSections';

// Anti-XSS: n'autorise que les liens http(s) (bloque javascript:, data:, etc.)
const safeHref = (url) => (/^https?:\/\//i.test((url || '').trim()) ? url : '#');


// Liste des scores médicaux connus avec leurs ID pour les liens
const KNOWN_SCORES = [
  // Cardiologie
  { patterns: ['CHA2DS2-VASc', 'CHA₂DS₂-VASc', 'CHADSVASC', 'CHADS2VASC', 'CHA2DS2VASc'], id: 'chadsvasc', name: 'CHA₂DS₂-VASc' },
  { patterns: ['HAS-BLED', 'HASBLED', 'HAS BLED'], id: 'hasbled', name: 'HAS-BLED' },
  { patterns: ['SCORE2', 'SCORE 2', 'SCORE2-OP'], id: 'score2', name: 'SCORE2' },
  { patterns: ['WELLS TVP', 'Wells TVP', 'score de Wells'], id: 'wells-tvp', name: 'Wells TVP' },
  { patterns: ['WELLS EP', 'Wells EP'], id: 'wells-ep', name: 'Wells EP' },
  { patterns: ['GRACE', 'score GRACE'], id: 'grace', name: 'GRACE' },
  { patterns: ['TIMI', 'score TIMI'], id: 'timi', name: 'TIMI' },
  { patterns: ['HEART', 'score HEART'], id: 'heart', name: 'HEART' },
  
  // Neurologie
  { patterns: ['NIHSS', 'NIH Stroke Scale'], id: 'nihss', name: 'NIHSS' },
  { patterns: ['Glasgow', 'GCS', 'score de Glasgow'], id: 'glasgow', name: 'Glasgow' },
  { patterns: ['ABCD2', 'ABCD²', 'score ABCD2'], id: 'abcd2', name: 'ABCD2' },
  
  // Pneumologie
  { patterns: ['CURB-65', 'CURB65', 'CRB-65', 'CRB65'], id: 'curb65', name: 'CURB-65' },
  { patterns: ['Fine', 'PSI', 'score de Fine', 'Pneumonia Severity Index'], id: 'fine', name: 'Fine/PSI' },
  { patterns: ['mMRC', 'MRC dyspnée', 'échelle mMRC'], id: 'mmrc', name: 'mMRC' },
  
  // Gastro-entérologie
  { patterns: ['Child-Pugh', 'Child Pugh', 'CHILD PUGH'], id: 'child-pugh', name: 'Child-Pugh' },
  { patterns: ['MELD', 'score MELD'], id: 'meld', name: 'MELD' },
  { patterns: ['Glasgow-Blatchford', 'Blatchford'], id: 'glasgow-blatchford', name: 'Glasgow-Blatchford' },
  { patterns: ['Rockall', 'score de Rockall'], id: 'rockall', name: 'Rockall' },
  
  // Néphrologie
  { patterns: ['DFG', 'DFGe', 'CKD-EPI', 'CKDEPI', 'clairance créatinine', 'Cockcroft'], id: 'dfg', name: 'DFG (CKD-EPI)' },
  
  // Psychiatrie
  { patterns: ['PHQ-9', 'PHQ9', 'PHQ 9'], id: 'phq9', name: 'PHQ-9' },
  { patterns: ['GAD-7', 'GAD7', 'GAD 7'], id: 'gad7', name: 'GAD-7' },
  { patterns: ['MMSE', 'Mini Mental', 'Mini-Mental'], id: 'mmse', name: 'MMSE' },
  { patterns: ['MoCA', 'Montreal Cognitive'], id: 'moca', name: 'MoCA' },
  
  // Douleur
  { patterns: ['EVA', 'échelle visuelle analogique', 'EN douleur', 'échelle numérique'], id: 'eva', name: 'EVA/EN' },
  { patterns: ['DN4', 'DN 4', 'douleur neuropathique'], id: 'dn4', name: 'DN4' },
  
  // Gériatrie
  { patterns: ['ADL', 'Katz', 'activités de vie quotidienne'], id: 'adl', name: 'ADL (Katz)' },
  { patterns: ['IADL', 'Lawton', 'activités instrumentales'], id: 'iadl', name: 'IADL (Lawton)' },
  { patterns: ['MNA', 'Mini Nutritional Assessment'], id: 'mna', name: 'MNA' },
  { patterns: ['GDS', 'Geriatric Depression Scale'], id: 'gds', name: 'GDS' },
  { patterns: ['Get Up and Go', 'Timed Up and Go', 'TUG'], id: 'tug', name: 'Timed Up and Go' },
  
  // Infectiologie
  { patterns: ['qSOFA', 'Quick SOFA', 'SOFA'], id: 'qsofa', name: 'qSOFA' },
  
  // Dermatologie
  { patterns: ['PASI', 'Psoriasis Area'], id: 'pasi', name: 'PASI' },
  { patterns: ['SCORAD', 'score SCORAD'], id: 'scorad', name: 'SCORAD' },
  { patterns: ['DLQI', 'Dermatology Life Quality'], id: 'dlqi', name: 'DLQI' },
  
  // Rhumatologie
  { patterns: ['DAS28', 'DAS 28', 'Disease Activity Score'], id: 'das28', name: 'DAS28' },
  { patterns: ['BASDAI', 'Bath Ankylosing'], id: 'basdai', name: 'BASDAI' },
  
  // Obstétrique
  { patterns: ['Bishop', 'score de Bishop'], id: 'bishop', name: 'Bishop' },
  { patterns: ['Apgar', 'score d\'Apgar'], id: 'apgar', name: 'Apgar' },
  
  // Diabétologie
  { patterns: ['FINDRISC', 'Finnish Diabetes Risk'], id: 'findrisc', name: 'FINDRISC' },
  
  // Risque opératoire
  { patterns: ['ASA', 'score ASA', 'classification ASA'], id: 'asa', name: 'ASA' },
  { patterns: ['Euroscore', 'EuroSCORE', 'EUROSCORE'], id: 'euroscore', name: 'EuroSCORE' },
  { patterns: ['Lee', 'RCRI', 'score de Lee'], id: 'lee', name: 'Lee/RCRI' }
];

// Fonction pour détecter et transformer les scores en liens
const renderTextWithScoreLinks = (text) => {
  if (!text) return text;
  
  let result = text;
  const replacements = [];
  
  // Trouver tous les scores mentionnés
  KNOWN_SCORES.forEach(score => {
    score.patterns.forEach(pattern => {
      // Créer une regex qui cherche le pattern avec des limites de mots
      const regex = new RegExp(`(${pattern.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        replacements.push({
          start: match.index,
          end: match.index + match[0].length,
          original: match[0],
          scoreId: score.id,
          scoreName: score.name
        });
      }
    });
  });
  
  // Trier par position (du dernier au premier pour ne pas décaler les index)
  replacements.sort((a, b) => b.start - a.start);
  
  // Dédupliquer (même position = même match)
  const seen = new Set();
  const uniqueReplacements = replacements.filter(r => {
    const key = `${r.start}-${r.end}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  
  if (uniqueReplacements.length === 0) {
    return text;
  }
  
  // Construire le résultat avec les liens
  const parts = [];
  let lastIndex = 0;
  
  // Remettre dans l'ordre croissant
  uniqueReplacements.sort((a, b) => a.start - b.start);
  
  uniqueReplacements.forEach(replacement => {
    // Ajouter le texte avant le match
    if (replacement.start > lastIndex) {
      parts.push(text.substring(lastIndex, replacement.start));
    }
    
    // Ajouter le lien vers le score
    parts.push(
      <Link
        key={`score-${replacement.start}`}
        to={`/scores?score=${replacement.scoreId}`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-500/30 border border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/50 hover:text-indigo-200 transition-all text-sm font-medium"
        title={`Calculer le score ${replacement.scoreName}`}
      >
        <Calculator className="w-3 h-3" />
        {replacement.original}
      </Link>
    );
    
    lastIndex = replacement.end;
  });
  
  // Ajouter le reste du texte
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts;
};

// Libellés courts et lisibles pour le sommaire de la fiche
const SHORT_LABELS = [
  [/URGENCE/, 'Urgence'], [/SYNTH/, 'Synthèse'], [/FORMES/, 'Formes cliniques'], [/CRIT/, 'Critères'],
  [/SCORES/, 'Scores'], [/DIFF/, 'Diagnostics diff.'], [/HYPOTH/, 'Hypothèses'], [/^DIAGNOSTIC$/, 'Diagnostic'],
  [/IMAGES/, 'Images'], [/EXAMENS COMPL/, 'Examens'], [/EXAMEN CLINIQUE/, 'Examen clinique'], [/BILAN/, 'Bilan'],
  [/ALERTE|DRAPEAU/, "Signes d'alerte"], [/TRAITEMENT/, 'Traitement'], [/CONDUITE/, 'Conduite à tenir'],
  [/SUIVI/, 'Suivi'], [/AVIS|ORIENTATION/, 'Orientation'], [/POINTS CL/, 'Points clés'], [/R[ÉE]F[ÉE]RENCES|SOURCES/, 'Sources'],
];
const shortLabel = (title) => {
  const u = (title || '').toUpperCase();
  const hit = SHORT_LABELS.find(([re]) => re.test(u));
  return hit ? hit[1] : title.charAt(0) + title.slice(1).toLowerCase();
};

export const DiagnosisResponse = ({ content, userQuery }) => {
  const parseUrgencyLevel = (text) => {
    // Match new format: "• Niveau: X → Délai: ..." or old format "Score: X"
    const newFormatMatch = text.match(/\[URGENCE\][\s\S]*?Niveau:\s*(\d)/i);
    const oldFormatMatch = text.match(/\[URGENCE\][\s\S]*?Score:\s*(\d)/i);
    const urgencyMatch = newFormatMatch || oldFormatMatch;
    
    // Extract delay - handle various formats
    const delayMatch = text.match(/Délai:\s*([^\n•]+)/i);
    
    // Extract "Si urgence:" line for additional context
    const siUrgenceMatch = text.match(/Si urgence:\s*([^\n]+)/i);
    
    if (urgencyMatch) {
      const score = parseInt(urgencyMatch[1]);
      const delay = delayMatch ? delayMatch[1].trim() : '';
      const siUrgence = siUrgenceMatch ? siUrgenceMatch[1].trim() : '';
      return { score, delay, siUrgence };
    }
    return null;
  };

  const urgency = parseUrgencyLevel(content);
  
  // IMPORTANT: Utiliser la requête utilisateur ORIGINALE pour trouver les images
  // Cela garantit une correspondance EXACTE avec la pathologie recherchée
  const relevantImages = findRelevantImages(userQuery || '');
  

  // Extract and render image links from content
  const renderContentWithImages = (text, isIllustrations = false) => {
    if (!text) return null;
    
    const API_URL = '' || '';
    
    const lines = text.split('\n');
    
    return lines.map((line, idx) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return null;
      
      // Check for URLs in the line
      const urlMatch = trimmedLine.match(/(https?:\/\/[^\s]+)/);
      
      if (urlMatch) {
        const originalUrl = urlMatch[1];
        const beforeUrl = trimmedLine.substring(0, trimmedLine.indexOf(originalUrl));
        const afterUrl = trimmedLine.substring(trimmedLine.indexOf(originalUrl) + originalUrl.length);
        
        // Use proxy URL for the image
        const proxyUrl = `${API_URL}/api/proxy/image?url=${encodeURIComponent(originalUrl)}`;
        
        // Determine the type of image based on URL or prefix
        let imageType = 'reference';
        let bgColor = 'bg-indigo-500/20';
        let borderColor = 'border-indigo-500/40';
        let textColor = 'text-indigo-400';
        
        if (originalUrl.includes('dermnetnz.org') || originalUrl.includes('dermato-info') || originalUrl.includes('dermis.net') || trimmedLine.includes('IMAGE_DERMATO')) {
          imageType = 'dermatologie';
          bgColor = 'bg-pink-500/20';
          borderColor = 'border-pink-500/40';
          textColor = 'text-pink-400';
        } else if (originalUrl.includes('radiopaedia') || trimmedLine.includes('IMAGE_RADIO')) {
          imageType = 'imagerie';
          bgColor = 'bg-cyan-500/20';
          borderColor = 'border-cyan-500/40';
          textColor = 'text-cyan-400';
        } else if (originalUrl.includes('litfl.com') || trimmedLine.includes('IMAGE_ECG')) {
          imageType = 'ECG';
          bgColor = 'bg-red-500/20';
          borderColor = 'border-red-500/40';
          textColor = 'text-red-400';
        } else if (trimmedLine.includes('SCHEMA_ANATOMIE')) {
          imageType = 'anatomie';
          bgColor = 'bg-emerald-500/20';
          borderColor = 'border-emerald-500/40';
          textColor = 'text-emerald-400';
        }

        // Clean description text
        const description = beforeUrl.replace(/^-\s*/, '').replace(/IMAGE_DERMATO:|IMAGE_RADIO:|IMAGE_ECG:|SCHEMA_ANATOMIE:/gi, '').trim();
        
        return (
          <div key={idx} className={`my-3 rounded-xl ${bgColor} border ${borderColor} overflow-hidden`}>
            {/* Image Preview using proxy */}
            <div className="relative bg-slate-900/50">
              <a href={safeHref(originalUrl)} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={proxyUrl}
                  alt={description || imageType}
                  className="w-full h-56 object-contain bg-slate-950 hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onError={(e) => {
                    // Si l'image ne charge pas via proxy, afficher un placeholder
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-56 items-center justify-center bg-slate-800">
                  <div className="text-center p-4">
                    <Image className={`w-12 h-12 mx-auto mb-2 ${textColor}`} />
                    <p className="text-slate-400 text-sm">Cliquez pour voir l'image</p>
                    <p className="text-slate-500 text-xs mt-1">Source: {imageType}</p>
                  </div>
                </div>
              </a>
              {/* Type badge overlay */}
              <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg ${bgColor} backdrop-blur-sm border ${borderColor}`}>
                <span className={`text-xs font-medium uppercase ${textColor}`}>{imageType}</span>
              </div>
            </div>
            
            {/* Description and link */}
            <div className="p-3">
              {description && (
                <p className="text-slate-200 text-sm mb-2">{description}</p>
              )}
              {afterUrl && <p className="text-slate-400 text-xs mb-2">{afterUrl}</p>}
              <a
                href={safeHref(originalUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 ${textColor} hover:text-white text-xs font-medium transition-colors`}
              >
                <ExternalLink className="w-3 h-3" />
                Ouvrir dans l'atlas
              </a>
            </div>
          </div>
        );
      }
      
      // Check for SCHEMA_ANATOMIE without URL (description only)
      if (trimmedLine.includes('SCHEMA_ANATOMIE:')) {
        const description = trimmedLine.replace(/^-\s*/, '').replace('SCHEMA_ANATOMIE:', '').trim();
        return (
          <div key={idx} className="my-2 p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/40">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <Image className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-emerald-400 uppercase font-medium mb-1">
                  Schéma anatomique
                </div>
                <div className="text-slate-200 text-sm">
                  {description}
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      // Regular line
      return (
        <div key={idx} className="text-slate-100">
          {trimmedLine}
        </div>
      );
    });
  };

  // Function to render text with clickable URLs
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    // URL regex pattern
    const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;
    
    const lines = text.split('\n');
    
    return lines.map((line, lineIdx) => {
      // First, process score links
      const lineWithScores = renderTextWithScoreLinks(line);
      
      // If lineWithScores is an array (contains score links), process differently
      if (Array.isArray(lineWithScores)) {
        return (
          <div key={lineIdx} className="text-slate-100">
            {lineWithScores.map((part, partIdx) => {
              if (typeof part === 'string') {
                // Process URLs in this string part
                const urlParts = part.split(urlPattern);
                if (urlParts.length === 1) {
                  return <span key={partIdx}>{part}</span>;
                }
                return urlParts.map((urlPart, urlIdx) => {
                  if (urlPart.match(urlPattern)) {
                    return (
                      <a
                        key={`${partIdx}-${urlIdx}`}
                        href={safeHref(urlPart)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors"
                      >
                        {urlPart.length > 50 ? urlPart.substring(0, 50) + '...' : urlPart}
                        <ExternalLink className="w-3 h-3 inline-block" />
                      </a>
                    );
                  }
                  return <span key={`${partIdx}-${urlIdx}`}>{urlPart}</span>;
                });
              }
              // It's a React element (score link), return as is
              return part;
            })}
          </div>
        );
      }
      
      // No score links, process URLs normally
      const parts = line.split(urlPattern);
      
      if (parts.length === 1) {
        // No URL found, return as is
        return (
          <div key={lineIdx} className="text-slate-100">
            {line}
          </div>
        );
      }
      
      // Render with clickable links
      return (
        <div key={lineIdx} className="text-slate-100">
          {parts.map((part, partIdx) => {
            if (part.match(urlPattern)) {
              return (
                <a
                  key={partIdx}
                  href={safeHref(part)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors"
                >
                  {part.length > 50 ? part.substring(0, 50) + '...' : part}
                  <ExternalLink className="w-3 h-3 inline-block" />
                </a>
              );
            }
            return <span key={partIdx}>{part}</span>;
          })}
        </div>
      );
    });
  };

  const parseResponse = (text) => {
    const sections = {
      urgence: {
        icon: AlertCircle,
        title: 'NIVEAU D\'URGENCE',
        bgColor: 'bg-red-900/40',
        borderColor: 'border-red-500/60',
        iconColor: 'text-red-400',
        titleColor: 'text-red-300'
      },
      synthese: { 
        icon: FileText, 
        title: 'SYNTHÈSE CLINIQUE',
        bgColor: 'bg-slate-900/40',
        borderColor: 'border-slate-600/40',
        iconColor: 'text-slate-400',
        titleColor: 'text-slate-300'
      },
      formes: { 
        icon: FileText, 
        title: 'FORMES CLINIQUES',
        bgColor: 'bg-indigo-900/30',
        borderColor: 'border-indigo-500/50',
        iconColor: 'text-indigo-400',
        titleColor: 'text-indigo-300'
      },
      criteres: { 
        icon: ClipboardCheck, 
        title: 'CRITÈRES DIAGNOSTIQUES',
        bgColor: 'bg-cyan-900/30',
        borderColor: 'border-cyan-500/50',
        iconColor: 'text-cyan-400',
        titleColor: 'text-cyan-300'
      },
      differentiels: { 
        icon: Stethoscope, 
        title: 'DIAGNOSTICS DIFFÉRENTIELS',
        bgColor: 'bg-amber-900/30',
        borderColor: 'border-amber-500/50',
        iconColor: 'text-amber-400',
        titleColor: 'text-amber-300'
      },
      bilan: { 
        icon: Microscope, 
        title: 'BILAN',
        bgColor: 'bg-blue-900/30',
        borderColor: 'border-blue-500/50',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-300'
      },
      pointscles: { 
        icon: AlertCircle, 
        title: 'POINTS CLÉS PRATIQUES',
        bgColor: 'bg-emerald-900/30',
        borderColor: 'border-emerald-500/50',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-300'
      },
      hypotheses: { 
        icon: Stethoscope, 
        title: 'HYPOTHÈSES DIAGNOSTIQUES',
        bgColor: 'bg-blue-900/30',
        borderColor: 'border-blue-500/50',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-300'
      },
      diagnostic: { 
        icon: Stethoscope, 
        title: 'DIAGNOSTIC',
        bgColor: 'bg-blue-900/30',
        borderColor: 'border-blue-500/50',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-300'
      },
      illustrations: { 
        icon: Image, 
        title: 'IMAGES ET SCHÉMAS DE RÉFÉRENCE',
        bgColor: 'bg-pink-900/30',
        borderColor: 'border-pink-500/50',
        iconColor: 'text-pink-400',
        titleColor: 'text-pink-300',
        isIllustrations: true
      },
      examens: { 
        icon: Microscope, 
        title: 'EXAMENS COMPLÉMENTAIRES',
        bgColor: 'bg-cyan-900/30',
        borderColor: 'border-cyan-500/50',
        iconColor: 'text-cyan-400',
        titleColor: 'text-cyan-300'
      },
      drapeaux: { 
        icon: AlertTriangle, 
        title: 'SIGNES D\'ALERTE',
        bgColor: 'bg-amber-900/30',
        borderColor: 'border-amber-500/50',
        iconColor: 'text-amber-400',
        titleColor: 'text-amber-300'
      },
      alerte: { 
        icon: AlertCircle, 
        title: 'SIGNES D\'ALERTE',
        bgColor: 'bg-red-900/30',
        borderColor: 'border-red-500/50',
        iconColor: 'text-red-400',
        titleColor: 'text-red-300'
      },
      traitement: { 
        icon: Pill, 
        title: 'TRAITEMENT',
        bgColor: 'bg-purple-900/30',
        borderColor: 'border-purple-500/50',
        iconColor: 'text-purple-400',
        titleColor: 'text-purple-300'
      },
      cat: { 
        icon: ClipboardCheck, 
        title: 'CONDUITE À TENIR',
        bgColor: 'bg-emerald-900/30',
        borderColor: 'border-emerald-500/50',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-300'
      },
      conduite: { 
        icon: ClipboardCheck, 
        title: 'CONDUITE À TENIR',
        bgColor: 'bg-emerald-900/30',
        borderColor: 'border-emerald-500/50',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-300'
      },
      suivi: { 
        icon: CalendarClock, 
        title: 'SUIVI ET SURVEILLANCE',
        bgColor: 'bg-teal-900/30',
        borderColor: 'border-teal-500/50',
        iconColor: 'text-teal-400',
        titleColor: 'text-teal-300'
      },
      scores: { 
        icon: Calculator, 
        title: 'SCORES CLINIQUES RECOMMANDÉS',
        bgColor: 'bg-violet-900/30',
        borderColor: 'border-violet-500/50',
        iconColor: 'text-violet-400',
        titleColor: 'text-violet-300'
      },
      examen: { 
        icon: Stethoscope, 
        title: 'EXAMEN CLINIQUE À RÉALISER',
        bgColor: 'bg-rose-900/30',
        borderColor: 'border-rose-500/50',
        iconColor: 'text-rose-400',
        titleColor: 'text-rose-300'
      },
      orientation: { 
        icon: Phone, 
        title: 'AVIS SPÉCIALISÉ',
        bgColor: 'bg-sky-900/30',
        borderColor: 'border-sky-500/50',
        iconColor: 'text-sky-400',
        titleColor: 'text-sky-300'
      },
      sources: { 
        icon: BookOpen, 
        title: 'RÉFÉRENCES',
        bgColor: 'bg-indigo-900/30',
        borderColor: 'border-indigo-500/50',
        iconColor: 'text-indigo-400',
        titleColor: 'text-indigo-300',
        hasLinks: true  // Enable clickable links for sources
      }
    };

    if (text.includes('[URGENCE]') || 
        text.includes('[SYNTHÈSE]') || text.includes('[SYNTHESE]') || 
        text.includes('[HYPOTHÈSES]') || text.includes('[HYPOTHESES]') ||
        text.includes('[DIAGNOSTIC]') || text.includes('[EXAMENS]') ||
        text.includes('[ALERTE]') || text.includes('[DRAPEAUX]') ||
        text.includes('[ILLUSTRATIONS]') || text.includes('[SUIVI]') ||
        text.includes('[TRAITEMENT]') ||
        text.includes('[EXAMEN]') || text.includes('[ORIENTATION]') ||
        text.includes('[SCORES]') || text.includes('[FORMES CLINIQUES]') ||
        text.includes('[CRITÈRES DIAGNOSTIQUES]') || text.includes('[BILAN]') ||
        text.includes('[DIAGNOSTICS DIFFÉRENTIELS]') || text.includes('[POINTS CLÉS]')) {
      
      const parsed = [];
      let currentSection = null;
      let currentContent = [];

      const lines = text.split('\n');
      
      for (const line of lines) {
        const trimmedLine = line.trim().toUpperCase();
        
        if (trimmedLine.includes('[URGENCE]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.urgence;
          currentContent = [];
        } else if (trimmedLine.includes('[SYNTHÈSE]') || trimmedLine.includes('[SYNTHESE]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.synthese;
          currentContent = [];
        } else if (trimmedLine.includes('[FORMES CLINIQUES]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.formes;
          currentContent = [];
        } else if (trimmedLine.includes('[CRITÈRES DIAGNOSTIQUES]') || trimmedLine.includes('[CRITERES DIAGNOSTIQUES]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.criteres;
          currentContent = [];
        } else if (trimmedLine.includes('[DIAGNOSTICS DIFFÉRENTIELS]') || trimmedLine.includes('[DIAGNOSTICS DIFFERENTIELS]') || trimmedLine.includes('[DDX]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.differentiels;
          currentContent = [];
        } else if (trimmedLine.includes('[BILAN]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.bilan;
          currentContent = [];
        } else if (trimmedLine.includes('[POINTS CLÉS]') || trimmedLine.includes('[POINTS CLES]') || trimmedLine.includes('[POINTS CLÉS PRATIQUES]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.pointscles;
          currentContent = [];
        } else if (trimmedLine.includes('[HYPOTHÈSES]') || trimmedLine.includes('[HYPOTHESES]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.hypotheses;
          currentContent = [];
        } else if (trimmedLine.includes('[DIAGNOSTIC]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.diagnostic;
          currentContent = [];
        } else if (trimmedLine.includes('[ILLUSTRATIONS]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.illustrations;
          currentContent = [];
        } else if (trimmedLine.includes('[EXAMENS]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.examens;
          currentContent = [];
        } else if (trimmedLine.includes('[DRAPEAUX]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.drapeaux;
          currentContent = [];
        } else if (trimmedLine.includes('[ALERTE]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.alerte;
          currentContent = [];
        } else if (trimmedLine.includes('[TRAITEMENT]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.traitement;
          currentContent = [];
        } else if (trimmedLine.includes('[CAT]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.cat;
          currentContent = [];
        } else if (trimmedLine.includes('[CONDUITE]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.conduite;
          currentContent = [];
        } else if (trimmedLine.includes('[SUIVI]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.suivi;
          currentContent = [];
        } else if (trimmedLine.includes('[SCORES]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.scores;
          currentContent = [];
        } else if (trimmedLine.includes('[EXAMEN]') && !trimmedLine.includes('[EXAMENS]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.examen;
          currentContent = [];
        } else if (trimmedLine.includes('[ORIENTATION]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.orientation;
          currentContent = [];
        } else if (trimmedLine.includes('[SOURCES]') || trimmedLine.includes('[SOURCE]')) {
          if (currentSection && currentContent.length > 0) {
            parsed.push({ ...currentSection, content: currentContent.join('\n') });
          }
          currentSection = sections.sources;
          currentContent = [];
        } else if (line.trim() && !line.trim().startsWith('━')) {
          currentContent.push(line);
        }
      }

      if (currentSection && currentContent.length > 0) {
        parsed.push({ ...currentSection, content: currentContent.join('\n') });
      }

      return parsed.length > 0 ? parsed : [{ 
        content: text, 
        ...sections.hypotheses
      }];
    }
    
    return [{ 
      content: text, 
      ...sections.hypotheses
    }];
  };

  const sections = parseResponse(content);

  // Sommaire : ids uniques par réponse + suivi de la section visible (scroll-spy)
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const rootRef = useRef(null);
  const navRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(null);
  const navSections = sections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.title !== 'NIVEAU D\'URGENCE' && !(section.isIllustrations && relevantImages.length > 0));
  const sectionEl = (index) => rootRef.current?.querySelector(`#${uid}-section-${index}`);

  const scrollToEl = (el) => {
    if (!el) return;
    const scroller = rootRef.current?.closest('.overflow-y-auto');
    const navH = navRef.current ? navRef.current.getBoundingClientRect().height : 0;
    if (scroller) {
      const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - navH - 12;
      scroller.scrollTo({ top, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - navH - 12, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || navSections.length === 0) return undefined;
    const scroller = rootRef.current?.closest('.overflow-y-auto') || window;
    let raf = null;
    const update = () => {
      raf = null;
      const limit = nav.getBoundingClientRect().bottom + 24;
      let current = navSections[0].index;
      navSections.forEach(({ index }) => {
        const el = sectionEl(index);
        if (el && el.getBoundingClientRect().top <= limit) current = index;
      });
      setActiveIdx(current);
    };
    const onScroll = () => { if (raf === null) raf = requestAnimationFrame(update); };
    update();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => { scroller.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, relevantImages.length]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || activeIdx === null) return;
    const chip = nav.querySelector(`[data-nav-index="${activeIdx}"]`);
    if (chip) {
      const left = chip.getBoundingClientRect().left - nav.getBoundingClientRect().left + nav.scrollLeft - 12;
      nav.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }, [activeIdx]);

  const getUrgencyBanner = () => {
    if (!urgency) return null;

    const { score, delay, siUrgence } = urgency;
    
    const urgencyConfig = {
      5: {
        bg: 'bg-red-600',
        border: 'border-red-500',
        text: 'text-white',
        icon: '🚨',
        title: 'URGENCE VITALE',
        action: 'APPELER LE 15 (SAMU) IMMÉDIATEMENT'
      },
      4: {
        bg: 'bg-orange-600',
        border: 'border-orange-500',
        text: 'text-white',
        icon: '⚠️',
        title: 'URGENCE',
        action: 'ORIENTATION URGENCES DANS LES 2H'
      },
      3: {
        bg: 'bg-yellow-600',
        border: 'border-yellow-500',
        text: 'text-gray-900',
        icon: '⏰',
        title: 'CONSULTATION RAPIDE',
        action: 'CONSULTATION DANS LES 24H'
      },
      2: {
        bg: 'bg-blue-600',
        border: 'border-blue-500',
        text: 'text-white',
        icon: '📅',
        title: 'CONSULTATION PROGRAMMÉE',
        action: 'CONSULTATION SOUS 7 JOURS'
      },
      1: {
        bg: 'bg-emerald-600',
        border: 'border-emerald-500',
        text: 'text-white',
        icon: '👁️',
        title: 'SURVEILLANCE',
        action: 'RÉÉVALUATION SI AGGRAVATION'
      }
    };

    const config = urgencyConfig[score] || urgencyConfig[1];

    return (
      <div data-testid="urgency-banner" className={`${config.bg} border sm:border-2 ${config.border} rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 ${score >= 4 ? 'animate-pulse' : ''}`}>
        <div className="flex items-start sm:items-center gap-2.5 sm:gap-3">
          <span className="text-2xl sm:text-3xl leading-none mt-0.5 sm:mt-0">{config.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
              <h3 className={`font-bold text-[15px] sm:text-lg leading-tight ${config.text}`}>
                {config.title} - NIVEAU {score}/5
              </h3>
              {delay && (
                <span className={`px-2.5 py-0.5 rounded-full bg-white/20 text-xs sm:text-sm font-semibold ${config.text}`}>
                  <Clock className="w-3.5 h-3.5 inline mr-1" />
                  {delay}
                </span>
              )}
            </div>
            <p className={`font-semibold text-sm sm:text-base ${config.text}`}>{config.action}</p>
            {siUrgence && (
              <p className={`text-sm mt-2 ${config.text} opacity-90`}>
                <span className="font-semibold">Orientation:</span> {siUrgence}
              </p>
            )}
            {score === 5 && (
              <a
                href="tel:15"
                className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-white text-red-600 rounded-full font-bold hover:bg-red-50 transition-all"
              >
                <Phone className="w-5 h-5" />
                APPELER LE 15
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={rootRef} className="space-y-3 sm:space-y-4 min-w-0">
      {getUrgencyBanner()}
      
      {/* Sommaire de la fiche — chips lisibles, section active surlignée (scroll-spy) */}
      {navSections.length > 3 && (
        <div className="sticky top-0 z-10 -mx-0.5">
          <div
            ref={navRef}
            data-testid="diagnosis-quick-nav"
            className="w-full max-w-full bg-slate-900/95 backdrop-blur-md rounded-xl px-2 py-2 mb-3 sm:mb-4 border border-slate-700/60 shadow-lg shadow-black/20 flex flex-nowrap sm:flex-wrap items-center gap-1.5 overflow-x-auto no-scrollbar snap-x scroll-px-3 [mask-image:linear-gradient(to_right,transparent,black_14px,black_calc(100%-14px),transparent)] sm:[mask-image:none]"
          >
            {relevantImages.length > 0 && (
              <button
                type="button"
                onClick={() => scrollToEl(rootRef.current?.querySelector(`#${uid}-images`))}
                className="snap-start shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[13px] font-medium whitespace-nowrap border bg-slate-800/90 border-slate-700 text-slate-100 hover:bg-slate-700 active:scale-95 transition-all"
                title="Images de référence"
              >
                <Image className="w-3.5 h-3.5 text-pink-400" />
                Photos
              </button>
            )}
            {navSections.map(({ section, index }) => {
              const Icon = section.icon;
              const active = activeIdx === index;
              return (
                <button
                  key={index}
                  type="button"
                  data-nav-index={index}
                  data-testid={`quick-nav-${index}`}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => scrollToEl(sectionEl(index))}
                  className={`snap-start shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[13px] font-medium whitespace-nowrap border transition-all duration-200 active:scale-95 ${
                    active
                      ? 'bg-indigo-600 border-indigo-400 text-white shadow-md shadow-indigo-600/30'
                      : 'bg-slate-800/90 border-slate-700 text-slate-100 hover:bg-slate-700 hover:border-slate-500'
                  }`}
                  title={section.title}
                >
                  <Icon className={`w-3.5 h-3.5 ${active ? 'text-white' : section.iconColor}`} />
                  {shortLabel(section.title)}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Images médicales pertinentes - affichées automatiquement via proxy */}
      {relevantImages.length > 0 && (
        <div id={`${uid}-images`} className="rounded-xl border sm:border-2 border-pink-500/50 bg-pink-900/30 p-3 sm:p-5 shadow-lg">
          <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-slate-700/50">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-pink-900/30 border border-pink-500/50 flex items-center justify-center shrink-0">
              <Image className="w-4 h-4 sm:w-5 sm:h-5 text-pink-400" />
            </div>
            <h3 className="font-bold text-[13px] sm:text-sm uppercase tracking-wide sm:tracking-wider text-pink-300 break-words">
              IMAGES DE RÉFÉRENCE - {relevantImages[0]?.name}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {relevantImages.map((condition, condIdx) => (
              condition.images.map((img, imgIdx) => {
                // Utiliser le proxy pour contourner CORS avec les images DermNet
                const API_URL = '' || '';
                const proxyImageUrl = `${API_URL}/api/proxy/image?url=${encodeURIComponent(img.url)}`;
                
                return (
                  <div key={`${condIdx}-${imgIdx}`} className="rounded-xl bg-slate-900/50 border border-pink-500/30 overflow-hidden">
                    <a href={img.url} target="_blank" rel="noopener noreferrer" className="block">
                      <img
                        src={proxyImageUrl}
                        alt={img.legend}
                        className="w-full h-48 object-contain bg-slate-950 hover:opacity-90 transition-opacity"
                        loading="eager"
                        onError={(e) => {
                          // Fallback: show placeholder instead of hiding
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="hidden w-full h-48 items-center justify-center bg-slate-800">
                        <div className="text-center p-4">
                          <Image className="w-10 h-10 mx-auto mb-2 text-pink-400" />
                          <p className="text-slate-400 text-sm">Cliquez pour voir l'image</p>
                          <p className="text-slate-500 text-xs mt-1">{img.source}</p>
                        </div>
                      </div>
                    </a>
                    <div className="p-3">
                      <p className="text-slate-200 text-sm">{img.legend}</p>
                      <p className="text-slate-500 text-xs mt-1">Source: {img.source}</p>
                    </div>
                  </div>
                );
              })
            ))}
          </div>
        </div>
      )}
      
      {sections.map((section, index) => {
        const Icon = section.icon;
        
        // Skip illustrations section if we already showed images from our database
        if (section.isIllustrations && relevantImages.length > 0) {
          return null;
        }
        
        // Skip URGENCE section as it's already shown in the banner
        if (section.title === 'NIVEAU D\'URGENCE') {
          return null;
        }
        
        return (
          <div
            key={index}
            id={`${uid}-section-${index}`}
            data-testid="ai-section-card"
            className={`rounded-xl border sm:border-2 ${section.borderColor} ${section.bgColor} p-3 sm:p-5 shadow-lg transition-all duration-300 hover:shadow-xl hover:brightness-105 animate-section-in min-w-0`}
            style={{
              animationDelay: `${index * 80}ms`,
              animationFillMode: 'backwards'
            }}
          >
            <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-slate-700/50">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${section.bgColor} border ${section.borderColor} flex items-center justify-center shrink-0 transition-transform duration-200 hover:scale-110`}>
                <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${section.iconColor}`} />
              </div>
              <h3 className={`font-bold text-[13px] sm:text-sm uppercase tracking-wide sm:tracking-wider leading-snug ${section.titleColor}`}>
                {section.title}
              </h3>
            </div>
            <div className="text-slate-100 leading-relaxed text-sm min-w-0">
              {section.isIllustrations ? (
                <div className="space-y-2">
                  {renderContentWithImages(section.content, true)}
                </div>
              ) : section.hasLinks ? (
                <div className="space-y-1.5 text-[15px] sm:text-sm leading-[1.65] sm:leading-relaxed break-words [&_a]:break-all">
                  {renderTextWithLinks(section.content)}
                </div>
              ) : (
                <RichText text={section.content} accent="indigo" />
              )}
            </div>
          </div>
        );
      })}

      {/* Disclaimer médico-légal */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-xl bg-slate-900/60 border border-slate-700 animate-section-in" style={{ animationDelay: `${sections.length * 80}ms` }}>
        <p className="text-xs text-slate-400 text-center">
          <span className="font-semibold">AIDE À LA DÉCISION MÉDICALE</span> - Cette analyse ne remplace pas l'examen clinique. 
          Le médecin reste seul responsable de sa décision diagnostique et thérapeutique. 
          En cas de doute, privilégier l'avis d'un confrère spécialiste.
        </p>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes section-in {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-section-in {
          animation: section-in 0.4s ease-out forwards;
        }
      `}</style>
      
      {/* Disclaimer réglementaire obligatoire */}
      <AIResponseDisclaimer />
    </div>
  );
};
